import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BookMarked, LogOut, ShieldCheck, Menu, X } from "lucide-react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isActive = (path) =>
    path === "/archive" ? location.pathname.startsWith("/archive") : location.pathname === path;

  return (
    <header className="sticky top-0 z-40 bg-surface border-b border-archive-border">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 py-4 flex items-center justify-between gap-3">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group min-w-0">
          <BookMarked strokeWidth={1.5} className="w-6 h-6 text-oxblood shrink-0" />
          <div className="leading-none min-w-0">
            <div className="font-serif text-xl sm:text-2xl text-ink tracking-tight truncate">
              The UGRR Ledger
            </div>
            <div className="hidden sm:block font-mono text-[10px] tracking-widest uppercase text-text-muted mt-1">
              A weekly reading · est. 1872
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 font-mono text-xs tracking-widest uppercase">
          {user ? (
            <>
              <Link to="/archive" data-testid="nav-archive" className={`link-underline pb-1 ${isActive("/archive") ? "text-oxblood" : "text-ink"}`}>
                Archive
              </Link>
              <Link to="/pages" data-testid="nav-pages" className={`link-underline pb-1 ${isActive("/pages") ? "text-oxblood" : "text-ink"}`}>
                Pages
              </Link>
              {!user.has_membership && user.role !== "admin" && (
                <Link to="/membership" data-testid="nav-membership" className={`link-underline pb-1 ${isActive("/membership") ? "text-oxblood" : "text-ink"}`}>
                  Membership
                </Link>
              )}
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="link-underline pb-1 flex items-center gap-1.5 text-ink">
                  <ShieldCheck strokeWidth={1.5} className="w-3.5 h-3.5" /> Archivist
                </Link>
              )}
              <span className="hidden lg:inline text-text-muted normal-case tracking-normal font-body">
                {user.name}
              </span>
              <button onClick={handleLogout} data-testid="logout-button" className="flex items-center gap-1.5 text-ink hover:text-oxblood transition-colors">
                <LogOut strokeWidth={1.5} className="w-3.5 h-3.5" /> Exit
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="link-underline pb-1 text-ink">
                Sign In
              </Link>
              <Link to="/register" data-testid="nav-register" className="bg-oxblood text-parchment px-4 py-2 hover:bg-oxblood-dark transition-colors">
                Join
              </Link>
            </>
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
          className="md:hidden text-ink p-2 -mr-2"
        >
          {open ? <X strokeWidth={1.5} className="w-6 h-6" /> : <Menu strokeWidth={1.5} className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <div className="md:hidden border-t border-archive-border bg-surface" data-testid="mobile-menu">
          <nav className="max-w-6xl mx-auto px-5 py-3 flex flex-col font-mono text-sm tracking-widest uppercase">
            {user ? (
              <>
                {user.name && (
                  <div className="px-2 py-3 font-body normal-case tracking-normal text-text-muted border-b border-archive-border mb-1">
                    Signed in as <span className="text-ink">{user.name}</span>
                  </div>
                )}
                <Link to="/archive" data-testid="m-nav-archive" className={`px-2 py-4 border-b border-archive-border ${isActive("/archive") ? "text-oxblood" : "text-ink"}`}>
                  Archive
                </Link>
                <Link to="/pages" data-testid="m-nav-pages" className={`px-2 py-4 border-b border-archive-border ${isActive("/pages") ? "text-oxblood" : "text-ink"}`}>
                  Pages
                </Link>
                {!user.has_membership && user.role !== "admin" && (
                  <Link to="/membership" data-testid="m-nav-membership" className={`px-2 py-4 border-b border-archive-border ${isActive("/membership") ? "text-oxblood" : "text-ink"}`}>
                    Membership
                  </Link>
                )}
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="m-nav-admin" className="px-2 py-4 border-b border-archive-border flex items-center gap-2 text-ink">
                    <ShieldCheck strokeWidth={1.5} className="w-4 h-4" /> Archivist
                  </Link>
                )}
                <button onClick={handleLogout} data-testid="m-logout-button" className="px-2 py-4 flex items-center gap-2 text-ink text-left">
                  <LogOut strokeWidth={1.5} className="w-4 h-4" /> Exit
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="m-nav-login" className="px-2 py-4 border-b border-archive-border text-ink">
                  Sign In
                </Link>
                <Link to="/register" data-testid="m-nav-register" className="mt-3 mb-2 bg-oxblood text-parchment px-4 py-4 text-center hover:bg-oxblood-dark transition-colors">
                  Join
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
